import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { AlertErrorIcon } from '@/components/icon'
import { SimpleLogoPageContainer } from '@/components/shared/SimpleLogoPageContainer'
import {
  Button,
  Center,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Flex,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
} from '@/components/ui'
import { ERROR_MESSAGES } from '@/constants/errorMessages'
import { ApiError, useMutation } from '@/lib/api'
import { forgotPassword } from '@/lib/auth'

const emailFormSchema = z.object({
  email: z
    .string()
    .min(1, ERROR_MESSAGES.emailRequired)
    .email(ERROR_MESSAGES.emailFormat),
})

type FormValue = z.infer<typeof emailFormSchema>

type Props = {
  email: string
  onChangeEmail: (email: string) => void
  onGoNext: () => void
}

export const ForgotPasswordInputEmail = ({
  email,
  onChangeEmail,
  onGoNext,
}: Props) => {
  const [apiError, setApiError] = useState<ApiError>()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormValue>({
    mode: 'onBlur',
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email,
    },
  })

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue('email', newValue)
    onChangeEmail(newValue)
  }

  const { request } = useMutation(forgotPassword, {
    onSuccess: () => {
      onGoNext()
    },
    onError: setApiError,
  })

  return (
    <SimpleLogoPageContainer>
      <Stack justify="start" align="center" gap="12" boxSize="full">
        <Stack gap="2" align="center">
          <Heading as="h1" fontSize="lg">
            パスワードリセット
          </Heading>
          <Text>
            パスワードリセット用の確認コードを送信しますので、登録しているメールアドレスを入力してください。
          </Text>
        </Stack>
        <Flex w="full" justify="center">
          <FieldRoot
            display="grid"
            alignItems="start"
            gridTemplateColumns={{ base: '1fr', desktop: 'max-content 1fr' }}
            gap={{ base: '1', desktop: '4' }}
            maxW="520px"
            invalid={!!errors.email}
          >
            <FieldLabel
              fontSize="md"
              fontWeight="normal"
              color="text.sub"
              h="10"
            >
              メールアドレス
            </FieldLabel>
            <Stack gap="0.5">
              <Input
                {...register('email')}
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="メールアドレスを入力してください"
              />
              <FieldErrorText>{errors.email?.message}</FieldErrorText>
            </Stack>
          </FieldRoot>
        </Flex>
        {!!apiError && (
          <HStack gap="3" justify="center" color="text.error">
            <AlertErrorIcon />
            <Text fontWeight="normal">{apiError.message}</Text>
          </HStack>
        )}
        <Center>
          <Button
            disabled={!email || !!errors.email}
            onClick={handleSubmit(async data => {
              await request({
                email: data.email,
              })
            })}
          >
            確認コードの送信
          </Button>
        </Center>
      </Stack>
    </SimpleLogoPageContainer>
  )
}
